import { logger } from '@/utils/logger/logger';
import { type UtmParams } from '@/utils/utmTracking';

export interface SdrDemoLeadParams {
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  countryCode?: string;
  phone?: string;
  company?: string;
  role?: string;
  specialty?: string;
  organisationSize?: string;
  demoReason?: string;
  utmParams?: UtmParams;
}

/**
 * Submits an SDR demo lead to HubSpot.
 *
 * Errors are caught and logged (never thrown) so legacy fire-and-forget
 * callers using `void submitSdrDemoLead({...})` keep working. Returns a
 * success boolean so newer UI callers can branch on the outcome — e.g.
 * the SLG flow needs to show an error and let the user retry instead of
 * advancing to the "demo booked" confirmation when HubSpot rejected it.
 */
export async function submitSdrDemoLead(params: SdrDemoLeadParams): Promise<boolean> {
  const {
    firstName,
    lastName,
    email,
    country,
    countryCode,
    phone,
    company,
    role,
    specialty,
    organisationSize,
    demoReason,
    utmParams,
  } = params;

  try {
    const response = await fetch('/api/hubspot/submit-sdr-demo-lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        firstname: firstName,
        lastname: lastName,
        email,
        country,
        countryCode,
        phone,
        company,
        jobtitle: role,
        specialty,
        organisationSize,
        demoReason,
        ...utmParams,
      }),
    });

    if (!response.ok) {
      const data = (await response.json()) as { message?: string };
      logger.warn('HubSpot SDR demo lead submission failed:', data.message);
      return false;
    }
    return true;
  } catch (error) {
    logger.warn('HubSpot SDR demo lead submission error:', error);
    return false;
  }
}
